import "./MetadataView.less";

import { Button, Dropdown, Menu, Spin } from "antd";
import { ClickParam } from "antd/lib/menu";
import { cloneDeep, differenceWith, intersectionWith, maxBy } from "lodash";
import { curry, __ } from "lodash/fp";
import * as React from "react";

import { DataFieldApi } from "lib/api";
import { CancellationToken } from "lib/api/base";
import { IBaseProps, IHighlightProps } from "lib/data/interfaces";
import {
	DataFieldTemplate,
	DataFieldValue,
	MetadataTemplate,
	MultipleItemRequirement,
	OrderedDataFieldValue,
	Permission,
	RequirementItem,
	SingleItemRequirement,
	StringDataFieldValue
} from "lib/data/model";
import { MultipleItemRequirementType } from "lib/data/model/enumerations";
import { DataFieldTemplateService, StateService } from "lib/services";

import { AuthorizedComponent, DataItem, GradientBar } from "components/general";


interface IMetadataViewProps extends IBaseProps, IHighlightProps {
	template?: MetadataTemplate;
	values: OrderedDataFieldValue[];
	requiredPermission?: Permission;
	isEditing?: boolean;
	onSave?: (values: OrderedDataFieldValue[]) => void;
}

interface IMetadataViewState {
	isLoading: boolean;
	isLoaded: boolean;
}


export class MetadataView extends React.Component<IMetadataViewProps, IMetadataViewState> {

	private apiCancellationToken: CancellationToken = new CancellationToken();


	private template?: MetadataTemplate;
	private values: OrderedDataFieldValue[] = [];

	private addableTemplates: Array<DataFieldTemplate | RequirementItem<DataFieldTemplate>> = [];
	private templateValues = Array<{ template: DataFieldTemplate, value: OrderedDataFieldValue }>();

	public constructor(props: IMetadataViewProps) {

		super(props);

		this.state = {
			isLoading: true,
			isLoaded: false
		};
	}

	public async componentWillMount() {

		// Store Template
		this.template = this.props.template;

		// Clone Values
		this.values = cloneDeep(this.props.values);

		// Process Data Fields
		await this.processFields();

		// Indicate Finished Loading
		StateService.setCancelableState(() => this.setState({ isLoading: false, isLoaded: true }), this.apiCancellationToken);
	}

	public async componentWillReceiveProps(newProps: IMetadataViewProps) {

		// Indicate Started Loading
		StateService.setCancelableState(() => this.setState({ isLoading: true, isLoaded: false }), this.apiCancellationToken);

		// Store Template
		this.template = newProps.template;

		// Clone Values
		this.values = cloneDeep(newProps.values);

		// Process Data Fields
		await this.processFields();

		// Indicate Finished Loading
		StateService.setCancelableState(() => this.setState({ isLoading: false, isLoaded: true }), this.apiCancellationToken);
	}

	public componentWillUnmount() {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();
	}

	public render() {

		// Define Class Name
		const className = "metadata-view" + (this.props.className ? ` ${this.props.className}` : "");

		// Sort Values
		const sortedItems = this.templateValues.sort((a, b) => this.sortDataFieldValues(a.value, b.value));

		// Check For Loading
		if (!this.state.isLoading) {

			const values = (

				<div className="metadata-view-values">

					{sortedItems.map((item, index) => {

						// Initialize Class Name
						let dataItemClassName: string = "metadata-view-item";

						// Check For Text Item
						if (item.template.field.dataType !== undefined
							&& (item.template.field.dataType.name === "Description"					// Remove This Condition Once Text Values Are Configured
								|| item.template.field.dataType.$type === StringDataFieldValue.$type)) {	// Change This To TextDataFieldValue.$type

							// Define Class Name
							dataItemClassName = "metadata-view-text-item";
						}

						return (

							<DataItem
								key={index}
								itemKey={index}
								className={dataItemClassName}
								template={item.template}
								value={item.value.item}
								highlightWords={this.props.highlightWords}
								requiredPermission={this.props.requiredPermission}
								isEditable={this.props.isEditing}
								onSave={curry(this.saveItem)(__, item.value)}
								actions={[
									<Button
										key="delete-data-item"
										icon="delete"
										type="ghost"
										onClick={curry(this.deleteItem)(__, item.value)}
									/>
								]}
							/>
						);
					})}

				</div>
			);

			// Check For Editable
			if (this.props.isEditing) {

				// Initialize Actions
				const actions: React.ReactElement[] = [];

				// Check For Addable Templates
				if (this.addableTemplates.length > 0) {

					actions.push(
						<AuthorizedComponent key="show-other-templates" permission={this.props.requiredPermission}>
							<Dropdown
								overlay={
									<Menu onClick={this.addItem}>
										{this.addableTemplates.map((item) => {

											switch (item.$type) {

												case DataFieldTemplate.$type:

													const dataFieldItem = (item as DataFieldTemplate);

													return (
														<Menu.Item key={`${dataFieldItem.field.id}`}>
															{dataFieldItem.field.name}
														</Menu.Item>
													);

												case RequirementItem.$type.DataFieldTemplate:

													const requirementItem = (item as RequirementItem<DataFieldTemplate>);

													return (
														<Menu.Item key={`${requirementItem.item.field.id}`}>
															{requirementItem.item.field.name}
														</Menu.Item>
													);

												default:

													return null;
											}
										})}
									</Menu>
								}
							>
								<Button icon="plus" type="ghost" />
							</Dropdown>
						</AuthorizedComponent>
					);
				}

				return (

					<section className={className}>

						<GradientBar
							className="metadata-view-bar"
							childrenClassName="metadata-view-bar-children"
							size="medium"
							title="Edit Data"
							highlightWords={this.props.highlightWords}
							actions={actions}
						>
							{values}
						</GradientBar>
					</section>
				);
			}
			else {

				return (

					<section className={className}>
						{values}
					</section>
				);
			}
		}
		else {

			return (

				<section className={className}>
					<Spin />
				</section>
			);
		}
	}

	private addItem = async (param: ClickParam) => {

		// Find Matching Data Field Template
		const matchingItemIndex = this.addableTemplates.findIndex((item) => {

			switch (item.$type) {

				case DataFieldTemplate.$type:

					const dataFieldItem = (item as DataFieldTemplate);

					return dataFieldItem.field.id.toString() === param.key;

				case RequirementItem.$type.DataFieldTemplate:

					const requirementItem = (item as RequirementItem<DataFieldTemplate>);

					return requirementItem.item.field.id.toString() === param.key;

				default:

					return false;
			}
		});

		// Check For Matching Template
		if (matchingItemIndex !== -1) {

			// Get Matching Item
			const matchingItem = this.addableTemplates[matchingItemIndex];

			// Initialize New Ordered Value
			let newOrderedValue: OrderedDataFieldValue;

			switch (matchingItem.$type) {

				case DataFieldTemplate.$type:

					const dataFieldItem = (matchingItem as DataFieldTemplate);

					const maxOrder = maxBy(this.templateValues, (i) => i.value.order);

					// Construct New Ordered Data Field Value
					newOrderedValue = new OrderedDataFieldValue();
					newOrderedValue.order = (maxOrder !== undefined ? maxOrder.value.order : 0);

					this.templateValues.push({ template: dataFieldItem, value: newOrderedValue });

					// Remove From Addable
					this.addableTemplates.splice(matchingItemIndex, 1);

					// Add To Values
					this.values.push(newOrderedValue);

					break;

				case RequirementItem.$type.DataFieldTemplate:

					const requirementItem = (matchingItem as RequirementItem<DataFieldTemplate>);

					// Construct New Ordered Data Field Value
					newOrderedValue = new OrderedDataFieldValue();
					newOrderedValue.order = requirementItem.initialOrder;

					this.templateValues.push({ template: requirementItem.item, value: newOrderedValue });

					// Remove From Addable
					this.addableTemplates.splice(matchingItemIndex, 1);

					// Add To Values
					this.values.push(newOrderedValue);

					break;
			}

			// Refresh Page
			this.forceUpdate();
		}
	}

	private saveItem = async (value: DataFieldValue | undefined, original: OrderedDataFieldValue) => {

		// Check For Success
		if (value !== undefined) {

			// Update Original With New Value
			original.item = value!;

			// Check For Save Handler
			if (this.props.onSave !== undefined) {

				// Return Values For Saving
				this.props.onSave(this.values);
			}
		}
	}

	private deleteItem = (_event: React.MouseEvent, item: OrderedDataFieldValue) => {

		// Find Item Index
		const existingItemIndex = this.values.findIndex((existingItem) => existingItem === item);

		// Check Index
		if (existingItemIndex !== -1) {

			// Remove Item From Values
			this.values.splice(existingItemIndex, 1);

			// Check For Save Handler
			if (this.props.onSave !== undefined) {

				// Return Values For Saving
				this.props.onSave(this.values);
			}

			// Re-Process Fields
			this.processFields();

			// Refresh Page
			this.forceUpdate();
		}
	}

	private processFields = async () => {

		// Clear Anything Previous
		this.addableTemplates = [];
		this.templateValues = [];

		// Loop Through Values
		this.values.forEach((orderedValue) => this.templateValues.push({ template: orderedValue.item.template, value: orderedValue }));

		// Get Fields From Values
		const valueFields = this.values.map((i) => i.item.template);

		// Check Template
		if (this.template !== undefined) {

			// Loop Through Requirements
			for (const requirement of this.template.requirements) {

				// Switch On Requirement Type
				switch (requirement.$type) {

					case SingleItemRequirement.$type.DataFieldTemplate:

						// Cast Requirement
						const singleItemRequirement = requirement as SingleItemRequirement<DataFieldTemplate>;

						// Check If Repeatable
						if (singleItemRequirement.isRepeatable) {

							// Get Requirement Template(s)
							const requirementTemplates = DataFieldTemplateService.getTemplatesFromRequirements([singleItemRequirement]);

							// Add Template Data Field To Addable List
							this.addableTemplates.push(...requirementTemplates.map((dataFieldTemplate) => {

								// Get Requirement Item For Template
								const requirementItem = DataFieldTemplateService.getRequirementItemFromTemplate([singleItemRequirement], dataFieldTemplate);

								// Return Requirement Item Or Data Field Template
								return (requirementItem !== undefined ? requirementItem : dataFieldTemplate);
							}));
						}
						else {

							// Check Values For Matching Data Field
							if (valueFields.find((value) => value.field.id === singleItemRequirement.item.item.field.id) === undefined) {

								// Return Requirement Item
								this.addableTemplates.push(singleItemRequirement.item);
							}
						}

						break;

					case MultipleItemRequirement.$type.DataFieldTemplate:

						// Cast Requirement
						const multipleItemRequirement = requirement as MultipleItemRequirement<DataFieldTemplate>;

						// Get Fields
						const requirementFields = multipleItemRequirement.items.map((i) => i.item);

						// Get All Fulfilled Fields
						const fulfilledFields = intersectionWith(valueFields, requirementFields, (a, b) => a.field.id === b.field.id);

						// Switch On Multiple Item Requirement Type
						switch (multipleItemRequirement.requirementType) {

							case MultipleItemRequirementType.AtLeast:

								this.addableTemplates.push(...differenceWith(requirementFields, valueFields, (a, b) => a.field.id === b.field.id).map((dataFieldTemplate) => {

									// Get Requirement Item For Template
									const requirementItem = DataFieldTemplateService.getRequirementItemFromTemplate([multipleItemRequirement], dataFieldTemplate);

									// Return Requirement Item Or Data Field Template
									return (requirementItem !== undefined ? requirementItem : dataFieldTemplate);
								}));

								break;

							case MultipleItemRequirementType.AtMost:

								// Check Fulfilled Fields
								if (fulfilledFields.length < multipleItemRequirement.fulfillmentCount) {

									this.addableTemplates.push(...differenceWith(requirementFields, valueFields, (a, b) => a.field.id === b.field.id).map((dataFieldTemplate) => {

										// Get Requirement Item For Template
										const requirementItem = DataFieldTemplateService.getRequirementItemFromTemplate([multipleItemRequirement], dataFieldTemplate);

										// Return Requirement Item Or Data Field Template
										return (requirementItem !== undefined ? requirementItem : dataFieldTemplate);
									}));
								}

								break;

							case MultipleItemRequirementType.Exactly:

								// Check Fulfilled Fields
								if (fulfilledFields.length < multipleItemRequirement.fulfillmentCount) {

									this.addableTemplates.push(...differenceWith(requirementFields, valueFields, (a, b) => a.field.id === b.field.id).map((dataFieldTemplate) => {

										// Get Requirement Item For Template
										const requirementItem = DataFieldTemplateService.getRequirementItemFromTemplate([multipleItemRequirement], dataFieldTemplate);

										// Return Requirement Item Or Data Field Template
										return (requirementItem !== undefined ? requirementItem : dataFieldTemplate);
									}));
								}

								break;

							case MultipleItemRequirementType.All:

								// Check Fulfilled Fields
								if (fulfilledFields.length < multipleItemRequirement.items.length) {

									this.addableTemplates.push(...differenceWith(requirementFields, valueFields, (a, b) => a.field.id === b.field.id).map((dataFieldTemplate) => {

										// Get Requirement Item For Template
										const requirementItem = DataFieldTemplateService.getRequirementItemFromTemplate([multipleItemRequirement], dataFieldTemplate);

										// Return Requirement Item Or Data Field Template
										return (requirementItem !== undefined ? requirementItem : dataFieldTemplate);
									}));
								}

								break;
						}

						break;
				}
			}
		}
		else {

			// Get All Available Fields
			const dataFields = await DataFieldApi.getDataFields();

			// Create Templates For All Data Fields
			const dataFieldTemplates = dataFields.map((field) => {

				const newTemplate = new DataFieldTemplate();
				newTemplate.field = field;
				newTemplate.name = "";
				newTemplate.stringPrefix = "";
				newTemplate.stringAppendage = "";

				return newTemplate;
			});

			// Add All Unfulfilled Fields
			this.addableTemplates.push(...differenceWith(dataFieldTemplates, valueFields, (a, b) => a.field.id === b.field.id));
		}
	}

	private sortDataFieldValues = (a: OrderedDataFieldValue, b: OrderedDataFieldValue): number => {

		let aOrder: number = 0;
		let bOrder: number = 0;

		// Check 'A' Order
		if (this.props.template && a.order === 0) {

			// Get 'A' Requirement Item
			const aRequirementItem = DataFieldTemplateService.getRequirementItemFromTemplate(this.props.template.requirements, a.item.template);

			// Check Requirement Item
			if (aRequirementItem) {

				aOrder = aRequirementItem.initialOrder;
			}
		}
		else if (a.order) {

			// Set 'A' Order
			aOrder = a.order;
		}

		// Check 'B' Order
		if (this.props.template && b.order === 0) {

			// Get 'B' Requirement Item
			const bRequirementItem = DataFieldTemplateService.getRequirementItemFromTemplate(this.props.template.requirements, b.item.template);

			// Check Requirement Item
			if (bRequirementItem) {

				bOrder = bRequirementItem.initialOrder;
			}
		}
		else if (b.order) {

			// Set 'B' Order
			bOrder = b.order;
		}

		return aOrder - bOrder;
	}
}
